import React, { useEffect, useState, useMemo } from "react";
import { Form, Table, Button, Modal } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { mapUserRoles } from "../../../../functions/useRoleMapping";
import useHandleBlur from "../../../../functions/useHandleBlur";
import { useFormState } from "../../../../functions/useFormState"; // Reusable form hook
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../../redux/slices/apiUrlSlice";
import { selectPumps, selectPumpDetails } from "../../../../redux/selectors";
import { postData, deleteData } from "../../../../redux/slices/apiCallSlice"; // Adjust the path as necessary
import { fetchJobDetails } from "../../../../redux/slices/fetchJobDetailsSlice"; // Adjust the path as necessary
import { fetchPumpUpdateData } from "../../../../redux/slices/hasPumpApiPing"; // Import the thunk
import { updateActivePumps } from '../../../../redux/slices/activePumpsSlice'; // Import the updateActivePumps action
import { clearSectionJobDetails } from "../../../../redux/slices/jobDetailsSlice";
import { selectApiStatus } from "../../../../redux/slices/apiStatusSlice"; 
import NoRecordFoundAlert from "../../../common/NoRecordFoundAlert";
import { postChangeLog } from "../../../../redux/slices/postChangeLogSlice"; // Import postChangeLog
import { getPumpDisplayLabel, normalizePumpId as normalizePumpIdValue } from "./pumpIdHelpers";

const normalizePumpId = (pump) => {
  const normalizedValue = normalizePumpIdValue(pump?.PumpID ?? pump?.pumpID);
  return !normalizedValue || normalizedValue === "unknown" ? "main" : normalizedValue;
};

const generatePumpIds = (pumps) => {
    let pumpCounter = 1;
    let jockeyCounter = 1;
    const order = ["pm", "jockey", "main", "water feature", "transfer", "recirculation", "trash", "sump", "custom"]; // Ensure all are lowercase
    const sortedPumps = [...pumps].sort((a, b) => {
    const aIndex = order.indexOf(normalizePumpId(a));
    const bIndex = order.indexOf(normalizePumpId(b));
        if (aIndex !== bIndex) {
            return aIndex - bIndex;
        }
        // Secondary sorting by VFD
        const aVFD = a.VFD ? parseInt(a.VFD.replace(/\D/g, '')) : Infinity;
        const bVFD = b.VFD ? parseInt(b.VFD.replace(/\D/g, '')) : Infinity;
        return aVFD - bVFD;
    });

    // Check if a jockey pump exists
    const jockeyExists = sortedPumps.some(pump => normalizePumpId(pump) === "jockey");
    if (jockeyExists) {
        pumpCounter = 2; // Start main count at 2 if jockey exists
    }

    const updatedPumps = sortedPumps.map((pump) => {
        const newPump = { ...pump }; // Create a new object to avoid modifying the original
        const pumpType = normalizePumpId(newPump);
        const pumpLabel = getPumpDisplayLabel(newPump.PumpID ?? newPump.pumpID);
        
        if (pump.IsCustom === "Yes" && pump.CustomPumpName) {
            newPump.DisplayPumpID = pump.CustomPumpName;
        } else if (pumpType === "jockey") {
            newPump.DisplayPumpID = `Jockey Pump ${jockeyCounter}`;
            jockeyCounter += 1;
        } else if (pumpType === "main") {
            if (newPump.Quantity > 1) {
                const start = pumpCounter;
                const end = pumpCounter + parseInt(newPump.Quantity) - 1;
                newPump.DisplayPumpID = `Main Pumps ${start}-${end}`;
                pumpCounter = end + 1;
            } else {
                newPump.DisplayPumpID = `Main Pump ${pumpCounter}`;
                pumpCounter += 1;
            }
        } else {
              newPump.DisplayPumpID = `${pumpLabel} Pump`;
        }
        return newPump;
    });
    return updatedPumps;
};

const isValueInDropdown = (value, dropdownOptions) => {
  const exists = dropdownOptions.some(
    (option) => option.DisplayText === value || option.displayName === value
  );
  return exists;
};

const PumpTable = ({ onPumpsChange, isJobMechInfo }) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails);
  const userRoles = useSelector(selectUserRoles);
  const currentUser = userDetails?.name;
  const sectionName = "pumpDetails";

  const jobId = useSelector((state) => state.jobId.jobId);
  const apiUrls = useSelector(selectApiUrls);
  const apiUrl = apiUrls[sectionName];
  const pumpApiUrl = apiUrls.pumps;
  const tableKey = sectionName;

  const storedJobDetails = useSelector(selectPumps);
  const pumpDetails = useSelector(selectPumpDetails);

  const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
  );

  const { formData, setFormData } = useFormState({});

  const jobDetails = useMemo(() => {
    const detail = storedJobDetails.find((detail) => detail.Zone === activetab);
    return detail || storedJobDetails[0] || {};

  }, [storedJobDetails, activetab]);

  const { handleFocus, handleBlur, showNotification, saveStatus } = useHandleBlur(formData);

  const { options } = useSelector((state) => state.dropdown); // Fetch dropdown values from redux


  /****************Add dropdown options here*************************/

  const genericOptions =
    options?.filter((option) => option.DropdownType === "Generic Yes/ No").sort((a, b) => a.SortOrder - b.SortOrder) ||
    [];
  const vfdOptions =
    options?.filter((option) => option.DropdownType === "VFD").sort((a, b) => a.SortOrder - b.SortOrder) || [];
    
  /******************************************************************/

  useEffect(() => {
    const filteredPumps = pumpDetails.filter(
      (pump) =>
        pump.Zone === activetab && pump.ProjectID === jobDetails.ProjectID
    );

    const initialFormData = filteredPumps.reduce((acc, pump) => {
      acc[pump.ID] = pump;
      return acc;
    }, {});

    setFormData(initialFormData);
  }, [pumpDetails, activetab, jobDetails, setFormData]);

  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
  });

  const [error, setError] = useState(null);
  const apiStatus = useSelector(selectApiStatus); // Fetch API status from Redux
  const loading = apiStatus[sectionName] !== "success"; // Determine loading state based on API status

  const [showModal, setShowModal] = useState(false);
  const [newPumpID, setNewPumpID] = useState("");
  const [customPumpName, setCustomPumpName] = useState("");
  const [newZone, setNewZone] = useState("1");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePumpId, setDeletePumpId] = useState(null);
  const [isAddPumpButtonDisabled, setIsAddPumpButtonDisabled] = useState(false);
  const [isCustomPump, setIsCustomPump] = useState(false);

  // Helper function to check if any field in the form has a "TBD" value, even if it's part of a larger string
  const isFieldTbd = (fieldValue) => {
    if (typeof fieldValue === "string") {
      fieldValue = fieldValue.trim().toLowerCase(); // Normalize to lowercase and remove extra whitespace
      return fieldValue.includes("tbd"); // Check if "tbd" is present anywhere in the string
    }
    return false;
  };

  // Helper function to evaluate an entire form entry (a specific pump) for TBD fields
  const evaluateZoneTbdFields = (pump) => {
    if (!pump) return {};

    return Object.keys(pump).reduce((acc, key) => {
      acc[key] = isFieldTbd(pump[key]);
      return acc;
    }, {});
  };

  useEffect(() => {
    setRoles((prevRoles) => ({ ...prevRoles, ...mapUserRoles(userRoles) }));
  }, [userRoles]);

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
    const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly; // Update isDisabled condition

  const handleChange = (e, pumpId) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [pumpId]: {
        ...prevData[pumpId],
        [name]: value,
      },
    }));
  };

  // Handle pump type selection - set custom flag when "Custom" is selected
  const handlePumpTypeChange = (e) => {
    const value = e.target.value;
    setNewPumpID(value);
    setIsCustomPump(value === "Custom");
    if (value !== "Custom") {
      setCustomPumpName("");
    }
  };

  const handleAddPump = async () => {
    setIsAddPumpButtonDisabled(true); // Disable the button
    
    // Check if custom pump name is provided when custom pump is selected
    if (isCustomPump && !customPumpName.trim()) {
      alert("Please enter a name for the custom pump");
      setIsAddPumpButtonDisabled(false);
      return;
    }
    
    const newPump = {
      pumpParentID: jobDetails.ID,
      projectID: jobDetails.ProjectID,
      saleQuoteID: jobDetails.SaleQuoteID,
      zone: newZone,
      pumpID: isCustomPump ? "Custom" : newPumpID,
      isCustom: isCustomPump ? "Yes" : "No",
      customPumpName: isCustomPump ? customPumpName : ""
    };

    try {
      await dispatch(postData({ url: apiUrl, data: newPump })).unwrap();
      
      // Post to change log after successful addition
      const pumpDescription = `Pump ${isCustomPump ? customPumpName : newPumpID}`;
      await dispatch(postChangeLog({
        projectID: jobId,
        currentUser: currentUser,
        userRole: Object.keys(roles).find(role => roles[role]) || "Unknown",
        section: "Pumps",
        field: pumpDescription,
        prevValue: "",
        newValue: "ADDED",
        dateTime: new Date().toISOString()
      })).unwrap();
      
      dispatch(clearSectionJobDetails(sectionName));
      await dispatch(fetchPumpUpdateData(jobDetails.ID)); // Dispatch the thunk
      const updatedDetails = await dispatch(fetchJobDetails({ jobId, apiUrls: { [sectionName]: apiUrl } })).unwrap();
      setFormData((prevData) => {
        const updatedFormData = { ...prevData };
        if (updatedDetails[sectionName]) {
          updatedDetails[sectionName].forEach((pump) => {
            if (pump.Zone === activetab && pump.ProjectID === jobDetails.ProjectID) {
              updatedFormData[pump.ID] = pump;
            }
          });
        }
        return updatedFormData;
      });
      setShowModal(false);   
      setIsAddPumpButtonDisabled(false); // Re-enable the button
      setCustomPumpName(""); // Reset custom pump name
      setIsCustomPump(false); // Reset custom pump flag

      // Ensure activePumps is correctly populated
      const activePumps = {
        SustainPump: updatedDetails.HasSustain || false,
        MainPump: updatedDetails.HasMain || false,
        WaterFeaturePump: updatedDetails.HasWaterFeature || false,
        TransferPump: updatedDetails.HasTransfer || false,
        RecirculationPump: updatedDetails.HasRecirc || false,
        TrashPump: updatedDetails.HasTrash || false,
        SumpPump: updatedDetails.HasSump || false,
        CustomPump: updatedDetails.HasCustom || false
      };
      dispatch(updateActivePumps({ jobId: jobDetails.ID, pumps: activePumps }));
      // Refetch both pump parent and details to get updated data after adding
      await dispatch(fetchJobDetails({ jobId, apiUrls: { [sectionName]: apiUrl } }));
      await dispatch(fetchJobDetails({ jobId, apiUrls: { "pumps": pumpApiUrl } }));
    
    } catch (error) {
      console.error("Failed to add pump:", error);
      setError(error.message || "An error occurred while adding the pump.");
      setIsAddPumpButtonDisabled(false); // Re-enable the button in case of error
    }
  };

// Removed redundant useEffect: Pump data is already loaded by fetchFullJob in JobHandler
// Only refetch after add/delete operations to get updated data

  const handleShowDeleteModal = (pumpId) => {
    setDeletePumpId(pumpId); // Set the pumpId you want to delete
    setShowDeleteModal(true); // Show the modal
  };
  
  const handleCloseDeleteModal = () => {
    setDeletePumpId(null); // Clear the pumpId when closing
    setShowDeleteModal(false); // Hide the modal
  };
  
  const handleConfirmDelete = async () => {
    if (deletePumpId) {
      await handleDeletePump(deletePumpId);
       dispatch(clearSectionJobDetails(sectionName));
      const updatedDetails = await dispatch(fetchJobDetails({ jobId, apiUrls: { [sectionName]: apiUrl } })).unwrap();
      await dispatch(fetchPumpUpdateData(jobDetails.ID)); // Dispatch the thunk

      setFormData((prevData) => {
        const updatedFormData = { ...prevData };
        if (updatedDetails[sectionName]) {
          updatedDetails[sectionName].forEach((pump) => {
            if (pump.Zone === activetab && pump.ProjectID === jobDetails.ProjectID) {
              updatedFormData[pump.ID] = pump;
            }
          });
        }
        return updatedFormData;
      });
      handleCloseDeleteModal();
      // Ensure activePumps is correctly populated
      const activePumps = {
        SustainPump: updatedDetails.HasSustain || false,
        MainPump: updatedDetails.HasMain || false,
        WaterFeaturePump: updatedDetails.HasWaterFeature || false,
        TransferPump: updatedDetails.HasTransfer || false,
        RecirculationPump: updatedDetails.HasRecirc || false,
        TrashPump: updatedDetails.HasTrash || false,
        SumpPump: updatedDetails.HasSump || false,
        CustomPump: updatedDetails.HasCustom || false
      };
      dispatch(updateActivePumps({ jobId: jobDetails.ID, pumps: activePumps }));
      // dispatch(updateActivePumps(jobDetails.ID)); // Dispatch the thunk to update active pumps
      dispatch(fetchJobDetails({ jobId, apiUrls: { [sectionName]: apiUrl } }));
       dispatch(fetchJobDetails({ jobId, apiUrls: { "pumps": pumpApiUrl } }));
      // onPumpsChange(); // Notify parent component
      // onRefresh(); // Trigger refresh after deleting pump
    }
  };

const handleDeletePump = async (pumpId) => {
    if (!pumpId) {
        setError("Invalid pump ID.");
        return;
    }

    try {
        // Find the pump to delete for change log
        const pumpToDelete = Object.values(formData).find(pump => pump.ID === pumpId);
        
        // Delete the specified pump and refetch job details
        await dispatch(deleteData({ url: apiUrl, deleteId: {ID: pumpId} })).unwrap();

        // Post to change log after successful deletion
        if (pumpToDelete) {
            const pumpDescription = `Pump ${pumpToDelete.PumpID || "Unknown"}`;
            
            await dispatch(postChangeLog({
                projectID: jobId,
                currentUser: currentUser,
                userRole: Object.keys(roles).find(role => roles[role]) || "Unknown",
                section: "Pumps",
                field: pumpDescription,
                prevValue: "",
                newValue: "DELETED",
                dateTime: new Date().toISOString()
            })).unwrap();
        }

        const updatedDetails = await dispatch(fetchJobDetails({ jobId, apiUrls: { [sectionName]: apiUrl } })).unwrap();

        setFormData((prevData) => {
            const updatedFormData = { ...prevData };
            if (updatedDetails[sectionName]) {
                updatedDetails[sectionName].forEach((pump) => {
                    if (pump.Zone === activetab && pump.ProjectID === jobDetails.ProjectID) {
                        updatedFormData[pump.ID] = pump;
                    }
                });
            }
            return updatedFormData;
        });
        dispatch(fetchPumpUpdateData(jobDetails.ID)); // Dispatch the thunk
        // Refetch pumps to get updated data after deletion
        dispatch(fetchJobDetails({ jobId, apiUrls: { pumps: pumpApiUrl } }));
    } catch (error) {
        console.error("Failed to delete pump:", error);
        setError(error.message || "An error occurred while deleting the pump.");
    }
};

// Removed redundant useEffect: Pump data is already loaded by fetchFullJob in JobHandler
// Only refetch after add/delete operations to get updated data

  const updatedFormData = useMemo(
    () => generatePumpIds(Object.values(formData)),
    [formData]
  );

  const existingPumpIDs = updatedFormData.map((pump) => normalizePumpId(pump));
  const pumpIDOptions = [
    { value: "pm", label: "PM" },
    { value: "jockey", label: "Jockey" },
    { value: "main", label: "Main" },
    { value: "water feature", label: "Water Feature" },
    { value: "transfer", label: "Transfer" },
    { value: "recirculation", label: "Recirculation" },
    { value: "trash", label: "Trash" },
    { value: "sump", label: "Sump" },
    { value: "Custom", label: "Custom" }
  ].filter(option => !["pm", "jockey"].includes(option.value) || !existingPumpIDs.includes(option.value));

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ); // Show loading spinner
  }

  if (error) {
    return <div className="error">{error}</div>; // Show error message if fetch fails
  }

  if (!loading && !jobDetails?.ID) {
    return (
      <NoRecordFoundAlert sectionName={sectionName} zone={activetab} />
    );
  }

  // Set default zone state to active tab on modal open
  const handleShowAddPumpModal = () => {
    setNewZone(activetab.toString());
    setNewPumpID("");
    setCustomPumpName("");
    setIsCustomPump(false);
    setShowModal(true);
  };

  return (
    <div className="pump-table-responsive">
      {showNotification && (
        <div className={`save-notification ${saveStatus.includes("Failed") ? "error" : ""}`}>
          {saveStatus}
        </div>
      )}
      <Table striped bordered hover className="pump-overview-table">
        <thead>
          <tr>
          <th >Pump ID</th>
        <th className="text-center px-2 py-1 col-sm-1">HP</th>
        <th className="text-center px-2 py-1 col-sm-1">RPM</th>
        <th className="text-center px-2 py-1 col-sm-1">GPM</th>
        <th className="text-center px-2 py-1 col-sm-1">HTR</th>
        <th className="text-center px-2 py-1 col-sm-1">XL</th>
        <th className="text-center px-2 py-1 col-sm-1">VFD</th>
        <th className="text-center px-2 py-1 col-sm-1">SS</th>
        <th className="text-center px-2 py-1 col-sm-1">EBV</th>
        <th className="text-center px-2 py-1 col-sm-1">Motor Temp Switch</th>
        <th className="text-center px-2 py-1 col-sm-1">Pump Temp Sensor</th>
        <th className="text-center px-2 py-1 col-sm-1">DV/DT Filters</th>
        <th className="text-center px-2 py-1 col-sm-1">Line Reactor</th>
            {isJobMechInfo && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {updatedFormData.map((pump) => {
            const zoneTbdFields = evaluateZoneTbdFields(pump); // Use the helper function to evaluate TBD fields

            return (
              <tr key={pump.ID}>
                <td className="pump-overview-identity">
                  {pump.DisplayPumpID}
                </td>
                <td data-label="HP">
                  <Form.Control
                    type="text"
                    name="MotorHP"
                    value={pump.MotorHP || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Motor HP", [], {
                        ID: pump.ID,
                        url: apiUrl,
                        field: "MotorHP",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={`pump-table ${zoneTbdFields?.MotorHP ? "tbd-highlight" : ""}`}
                    onChange={(e) => handleChange(e, pump.ID)}
                  />
                </td>
                <td data-label="RPM">
                  <Form.Control
                    type="text"
                    name="PumpRPM"
                    value={pump.PumpRPM || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Pump RPM", [], {
                        ID: pump.ID,
                        url: apiUrl,
                        field: "PumpRPM",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                    className={`pump-table ${zoneTbdFields?.PumpRPM ? "tbd-highlight" : ""}`}
                    onChange={(e) => handleChange(e, pump.ID)}
                  />
                </td>
                <td data-label="GPM">
                  <Form.Control
                    type="text"
                    name="PumpFlow"
                    value={pump.PumpFlow || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Pump Flow", [], {
                        ID: pump.ID,
                        url: apiUrl,
                        field: "PumpFlow",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                    className={`pump-table ${zoneTbdFields?.PumpFlow ? "tbd-highlight" : ""}`}
                    onChange={(e) => handleChange(e, pump.ID)}
                  />
                </td>
                <td data-label="HTR">
                  <Form.Select
                    name="MotorWindingHeaters"
                    value={pump.MotorWindingHeaters || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Motor Winding Heaters", genericOptions, {
                        ID: pump.ID,
                        url: apiUrl,
                        field: "MotorWindingHeaters",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={`pump-table ${zoneTbdFields?.MotorWindingHeaters ? "tbd-highlight" : ""}`}
                    onChange={(e) => handleChange(e, pump.ID)}
                  >
                    <option value="">Select</option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                    {!isValueInDropdown(pump.MotorWindingHeaters, genericOptions) &&
                      pump.MotorWindingHeaters && (
                        <option value={pump.MotorWindingHeaters} style={{ color: "red" }}>
                          *{pump.MotorWindingHeaters}
                        </option>
                      )}
                  </Form.Select>
                </td>
                <td data-label="XL">
                  <Form.Select
                    name="XL"
                    value={pump.XL || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "XL", genericOptions, {
                        ID: pump.ID,
                        url: apiUrl,
                        field: "XL",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={`pump-table ${zoneTbdFields?.XL ? "tbd-highlight" : ""}`}
                    onChange={(e) => handleChange(e, pump.ID)}
                  >
                    <option value="">Select</option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                    {!isValueInDropdown(pump.XL, genericOptions) &&
                      pump.XL && (
                        <option value={pump.XL} style={{ color: "red" }}>
                          *{pump.XL}
                        </option>
                      )}
                  </Form.Select>
                </td>
                <td data-label="VFD">
                  <Form.Select
                    name="VFD"
                    value={pump.VFD || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "VFD", genericOptions, {
                        ID: pump.ID,
                        url: apiUrl,
                        field: "VFD",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={`pump-table ${zoneTbdFields?.VFD ? "tbd-highlight" : ""}`}
                    onChange={(e) => handleChange(e, pump.ID)}
                  >
                    <option value="">Select</option>
                    {vfdOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                    {!isValueInDropdown(pump.VFD, vfdOptions) &&
                      pump.VFD && (
                        <option value={pump.VFD} style={{ color: "red" }}>
                          *{pump.VFD}
                        </option>
                      )}
                  </Form.Select>
                </td>
                <td data-label="SS">
                  <Form.Select
                    name="SS"
                    value={pump.SS || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "SS", genericOptions, {
                        ID: pump.ID,
                        url: apiUrl,
                        field: "SS",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={`pump-table ${zoneTbdFields?.SS ? "tbd-highlight" : ""}`}
                    onChange={(e) => handleChange(e, pump.ID)}
                  >
                    <option value="">Select</option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                    {!isValueInDropdown(pump.SS, genericOptions) &&
                      pump.SS && (
                        <option value={pump.SS} style={{ color: "red" }}>
                          *{pump.SS}
                        </option>
                      )}
                  </Form.Select>
                </td>
                <td data-label="EBV">
                  <Form.Select
                    name="EBV"
                    value={pump.EBV || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "EBV", genericOptions, {
                        ID: pump.ID,
                        url: apiUrl,
                        field: "EBV",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={`pump-table ${zoneTbdFields?.EBV ? "tbd-highlight" : ""}`}
                    onChange={(e) => handleChange(e, pump.ID)}
                  >
                    <option value="">Select</option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                    {!isValueInDropdown(pump.EBV, genericOptions) &&
                      pump.EBV && (
                        <option value={pump.EBV} style={{ color: "red" }}>
                          *{pump.EBV}
                        </option>
                      )}
                  </Form.Select>
                </td>
                <td data-label="Motor Temp Switch">
                  <Form.Select
                    name="MotorTempSwitch"
                    value={pump.MotorTempSwitch || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Motor Temp Switch", genericOptions, {
                        ID: pump.ID,
                        url: apiUrl,
                        field: "MotorTempSwitch",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={`pump-table ${zoneTbdFields?.MotorTempSwitch ? "tbd-highlight" : ""}`}
                    onChange={(e) => handleChange(e, pump.ID)}
                  >
                    <option value="">Select</option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                    {!isValueInDropdown(pump.MotorTempSwitch, genericOptions) &&
                      pump.MotorTempSwitch && (
                        <option value={pump.MotorTempSwitch} style={{ color: "red" }}>
                          *{pump.MotorTempSwitch}
                        </option>
                      )}
                  </Form.Select>
                </td>
                <td data-label="Pump Temp Sensor">
                  <Form.Select
                    name="PumpTempSensor"
                    value={pump.PumpTempSensor || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Pump Temp Sensor", genericOptions, {
                        ID: pump.ID,
                        url: apiUrl,
                        field: "PumpTempSensor",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={`pump-table ${zoneTbdFields?.PumpTempSensor ? "tbd-highlight" : ""}`}
                    onChange={(e) => handleChange(e, pump.ID)}
                  >
                    <option value="">Select</option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                    {!isValueInDropdown(pump.PumpTempSensor, genericOptions) &&
                      pump.PumpTempSensor && (
                        <option value={pump.PumpTempSensor} style={{ color: "red" }}>
                          *{pump.PumpTempSensor}
                        </option>
                      )}
                  </Form.Select>
                </td>
                <td data-label="DV/DT Filters">
                  <Form.Select
                    name="DVDT"
                    value={pump.DVDT || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "DV/DT Filters", genericOptions, {
                        ID: pump.ID,
                        url: apiUrl,
                        field: "DVDT",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={`pump-table ${zoneTbdFields?.DVDT ? "tbd-highlight" : ""}`}
                    onChange={(e) => handleChange(e, pump.ID)}
                  >
                    <option value="">Select</option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                    {!isValueInDropdown(pump.DVDT, genericOptions) &&
                      pump.DVDT && (
                        <option value={pump.DVDT} style={{ color: "red" }}>
                          *{pump.DVDT}
                        </option>
                      )}
                  </Form.Select>
                </td>
                <td data-label="Line Reactor">
                  <Form.Select
                    name="LineReactor"
                    value={pump.LineReactor || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Line Reactor", genericOptions, {
                        ID: pump.ID,
                        url: apiUrl,
                        field: "LineReactor",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={`pump-table ${zoneTbdFields?.LineReactor ? "tbd-highlight" : ""}`}
                    onChange={(e) => handleChange(e, pump.ID)}
                  >
                    <option value="">Select</option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                    {!isValueInDropdown(pump.LineReactor, genericOptions) &&
                      pump.LineReactor && (
                        <option value={pump.LineReactor} style={{ color: "red" }}>
                          *{pump.LineReactor}
                        </option>
                      )}
                  </Form.Select>
                </td>
                {isJobMechInfo && (
                  <td data-label="Actions" className="pump-overview-actions">
                    <Button
                      variant="link"
                      onClick={() => handleShowDeleteModal(pump.ID)}
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      aria-label={`Delete ${pump.DisplayPumpID}`}
                    >
                      <i className="bi bi-trash"></i>
                    </Button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </Table>

      {isJobMechInfo && (roles.Admin || roles.Apps) && (
        <Button variant="primary" className="mb-3 pump-add-button" onClick={handleShowAddPumpModal} disabled={isAddPumpButtonDisabled || readOnly}>
          Add Pump
        </Button>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} className="pump-table-modal">
        <Modal.Header closeButton>
          <Modal.Title>Add Pump</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="newPumpID">
            <Form.Label>Pump Type</Form.Label>
            <Form.Select
              value={newPumpID}
              onChange={handlePumpTypeChange}
            >
              <option value="">Select Pump Type</option>
              {pumpIDOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          
          {isCustomPump && (
            <Form.Group controlId="customPumpName" className="mt-3">
              <Form.Label>Custom Pump Name</Form.Label>
              <Form.Control
                type="text"
                value={customPumpName}
                onChange={(e) => setCustomPumpName(e.target.value)}
                placeholder="Enter custom pump name"
              />
            </Form.Group>
          )}
          
          <Form.Group controlId="newZone" className="mt-3">
            <Form.Label>Zone</Form.Label>
            <Form.Select
              value={newZone}
              onChange={(e) => setNewZone(e.target.value)}
            >
              <option value="1">Zone 1</option>
              <option value="2">Zone 2</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button 
            variant="primary" 
            disabled={isAddPumpButtonDisabled || !newPumpID || (isCustomPump && !customPumpName.trim())} 
            onClick={handleAddPump}
          >
            Add Pump
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} className="pump-table-modal">
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this item?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PumpTable;
